import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Can } from '../../contexts/AbilityContext';
import PageTitle from '../../components/shared/PageTitle';
import { useGetUseCasesQuery, useGetTestimonialRequestsQuery, usePublishTestimonialMutation, useGetVendorMeQuery } from '../../store/vendorApi';
import { useGetTeamMembersQuery } from '../../store/teamMemberApi';
import { useGetPromotersQuery } from '../../store/promoterApi';
import { Avatar } from '@clareefai/ui';
import ShareButtons from '../../components/public/ShareButtons';
import { PUBLIC_SITE_URL } from '../../utils/publicSite';
import {
  Lightbulb, CheckCircle, Star, Eye,
  Plus, Search, Send, FileText, Download,
  Share2, ExternalLink, Grid, List, Loader2
} from 'lucide-react';

const PublishToggle: React.FC<{
  isPublished: boolean;
  isLoading: boolean;
  onToggle: () => void;
}> = ({ isPublished, isLoading, onToggle }) => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
      <span className={`text-xs font-medium ${isPublished ? 'text-green-700' : 'text-gray-500'}`}>
        {isPublished ? t('useCases.publishToggle.published') : t('useCases.publishToggle.draft')}
      </span>
      <button
        role="switch"
        aria-checked={isPublished}
        disabled={isLoading}
        onClick={onToggle}
        className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
          isPublished ? 'bg-green-500' : 'bg-gray-300'
        }`}
      >
        <span
          className={`pointer-events-none inline-flex h-4 w-4 transform items-center justify-center rounded-full bg-white shadow-sm ring-0 transition-transform duration-200 mt-1 ${
            isPublished ? 'translate-x-6 ml-[-2px]' : 'translate-x-1'
          }`}
        >
          {isLoading && <Loader2 className="w-3 h-3 text-gray-400 animate-spin" />}
        </span>
      </button>
    </div>
  );
};

const UseCases: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'our_requests' | 'promoter_submissions' | 'published' | 'featured'>('promoter_submissions');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [repFilter, setRepFilter] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [publishTestimonial] = usePublishTestimonialMutation();
  const [publishingIds, setPublishingIds] = useState<Set<string>>(new Set());
  const { data: vendor } = useGetVendorMeQuery();
  const { data: teamMembersData = [] } = useGetTeamMembersQuery();
  const { data: promotersData = [] } = useGetPromotersQuery();
  const vendorSlug = vendor?.pageInfo?.pageSlug || '';

  const handleTogglePublish = async (useCase: any) => {
    if (!useCase.id || !useCase.promoterId) return;
    const isCurrentlyPublished = useCase.status === 'PUBLISHED' || useCase.status === 'FEATURED';
    setPublishingIds((prev) => new Set(prev).add(useCase.id));
    try {
      await publishTestimonial({
        promoterId: useCase.promoterId,
        testimonialId: useCase.id,
        isPublished: !isCurrentlyPublished,
      }).unwrap();
    } catch (error) {
      console.error('Error toggling publish status:', error);
    } finally {
      setPublishingIds((prev) => {
        const next = new Set(prev);
        next.delete(useCase.id);
        return next;
      });
    }
  };

  const getShareUrl = (id: string) =>
    `${PUBLIC_SITE_URL}/use-case/${id}${vendorSlug ? `?slug=${vendorSlug}` : ''}`;

  // Single request: fetch all use cases, then filter on UI
  const { data: useCasesByStatus, isLoading, error: errorUseCases } = useGetUseCasesQuery();
  const completedUseCases = useMemo(() => useCasesByStatus?.completed ?? [], [useCasesByStatus?.completed]);
  const publishedUseCases = useMemo(() => useCasesByStatus?.published ?? [], [useCasesByStatus?.published]);
  const allFeaturedUseCasesList = useMemo(() => useCasesByStatus?.featured ?? [], [useCasesByStatus?.featured]);

  const allPromoterSubmissions = useMemo(
    () => [...completedUseCases, ...publishedUseCases, ...allFeaturedUseCasesList],
    [completedUseCases, publishedUseCases, allFeaturedUseCasesList]
  );
  const allPublishedUseCases = publishedUseCases;
  const allFeaturedUseCases = allFeaturedUseCasesList;

  // Fetch testimonial requests from API
  const { data: testimonialRequests = [], isLoading: loadingRequests } = useGetTestimonialRequestsQuery({
    type: 'USE_CASE'
  });

  // DEV-91 — use-case validation requests no longer expire. Legacy expiresAt/isExpired
  // fields from the API (if still present) are ignored; the scheduler handles nudging
  // the promoter via auto-reminders (DEV-92) instead.
  const ourUseCaseRequests = useMemo(() => {
    return testimonialRequests.map(request => {
      // The four API statuses are exhaustive, so REJECTED is the final branch
      // (no dead 'pending' fallback). 'sent' = awaiting the promoter's response.
      const status = request.status === 'VENDOR_REQUESTED' ? 'sent' :
              request.status === 'PROMOTER_APPROVED' ? 'accepted' :
              request.status === 'VENDOR_APPROVED' ? 'approved' :
              'declined';
      return {
        id: request.id,
        promoterName: request.promoterName,
        promoterCompany: request.promoterCompany || '',
        promoterAvatar: '', // Will need to get from promoter data if needed
        requestDate: request.requestedAt,
        status,
        emailSent: true, // Email is sent when request is created
        reminderCount: request.reminderCount ?? 0,
        lastReminder: request.lastReminderSentAt ?? null,
        autoReminderEnabled: request.autoReminderEnabled ?? true,
        product: request.productId || '',
        industry: '',
        companySize: '',
        conversionValue: 0,
        notes: request.rejectionReason || '',
        type: 'our_request'
      };
    });
  }, [testimonialRequests]);

  const allOurRequests = ourUseCaseRequests;

  const getSubmissionStatusBadge = (status: string) => {
    const badges = {
      COMPLETED: 'bg-gray-100 text-gray-800',
      PUBLISHED: 'bg-green-100 text-green-800',
      FEATURED: 'bg-purple-100 text-purple-800'
    };

    const labelKeys: Record<string, string> = {
      COMPLETED: 'useCases.status.completed',
      PUBLISHED: 'useCases.status.published',
      FEATURED: 'useCases.status.featured'
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${badges[status as keyof typeof badges] || 'bg-gray-100 text-gray-800'}`}>
        {labelKeys[status] ? t(labelKeys[status]) : status}
      </span>
    );
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      sent: 'bg-blue-100 text-blue-800',
      accepted: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      declined: 'bg-red-100 text-red-800',
      approved: 'bg-green-100 text-green-800',
      published: 'bg-green-100 text-green-800',
      featured: 'bg-yellow-100 text-yellow-800'
    };

    const labelKeys: Record<string, string> = {
      sent: 'useCases.status.requested',
      accepted: 'useCases.status.promoterApproved',
      pending: 'useCases.status.pending',
      declined: 'useCases.status.rejected',
      approved: 'useCases.status.approved',
      published: 'useCases.status.published',
      featured: 'useCases.status.featured'
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badges[status as keyof typeof badges] || 'bg-gray-100 text-gray-800'}`}>
        {labelKeys[status] ? t(labelKeys[status]) : status}
      </span>
    );
  };



  const handleUseCaseClick = (useCase: any) => {
    if (activeTab === 'promoter_submissions') {
      navigate(`/use-case-submission/${useCase.id}`, { state: { testimonial: useCase } });
    } else if (activeTab === 'published' || activeTab === 'featured') {
      navigate(`/use-cases/published/${useCase.id}`, { state: { useCase } });
    }
  };

  const handleRequestClick = (request: any) => {
    navigate(`/testimonial-requests/${request.id}`);
  };

  const escapeCsvField = (value: string | number | boolean | null | undefined) => {
    const str = String(value ?? '');
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const parseUseCaseContent = (content: string | undefined) => {
    if (!content) return '';
    if (typeof content === 'string' && content.startsWith('{')) {
      try {
        return JSON.parse(content)?.generated_use_case || content;
      } catch {
        return content;
      }
    }
    return content;
  };

  const downloadUseCasesCsv = (useCases: typeof allPromoterSubmissions, filename: string) => {
    const headers = ['Name', 'Company', 'Title', 'Product', 'Status', 'Featured', 'Industry', 'Company Size', 'Content', 'Submitted Date', 'Reviewed Date'];
    const rows = useCases.map((u) => [
      escapeCsvField(u.promoterName),
      escapeCsvField(u.promoterCompany),
      escapeCsvField(u.useCaseTitle),
      escapeCsvField(u.productId),
      escapeCsvField(u.status),
      escapeCsvField(u.isFeatured ? 'Yes' : 'No'),
      escapeCsvField(u.useCaseIndustry ?? u.industry),
      escapeCsvField(u.useCaseCompanySize ?? u.companySize),
      escapeCsvField(parseUseCaseContent(u.content)),
      escapeCsvField(u.createdAt ? new Date(u.createdAt).toLocaleDateString('en-US') : ''),
      escapeCsvField(u.reviewedAt ? new Date(u.reviewedAt).toLocaleDateString('en-US') : ''),
    ]);
    const csv = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };


  // Compact inline stats shown top-right of the page header, switched by the
  // active tab — mirrors the Reviews Management layout for cross-page consistency.
  const renderInlineStats = () => {
    const stats = activeTab === 'our_requests'
      ? [
          { value: ourUseCaseRequests.length, color: 'text-blue-600', label: t('useCases.stats.totalRequestsSent') },
          { value: ourUseCaseRequests.filter(r => r.status === 'accepted').length, color: 'text-green-600', label: t('useCases.stats.accepted') },
          { value: ourUseCaseRequests.filter(r => r.status === 'sent').length, color: 'text-yellow-600', label: t('useCases.stats.pending') },
          { value: ourUseCaseRequests.filter(r => r.status === 'declined').length, color: 'text-red-600', label: t('useCases.stats.declined') },
        ]
      : activeTab === 'promoter_submissions'
      ? [
          { value: allPromoterSubmissions.length, color: 'text-purple-600', label: t('useCases.stats.totalSubmissions') },
          { value: completedUseCases.length, color: 'text-gray-600', label: t('useCases.stats.notPublished') },
          { value: publishedUseCases.length, color: 'text-green-600', label: t('useCases.stats.published') },
          { value: allFeaturedUseCases.length, color: 'text-purple-600', label: t('useCases.stats.featured') },
        ]
      : activeTab === 'published'
      ? [
          { value: publishedUseCases.length, color: 'text-green-600', label: t('useCases.stats.published') },
          { value: allFeaturedUseCases.length, color: 'text-blue-600', label: t('useCases.stats.featured') },
          { value: completedUseCases.length, color: 'text-gray-600', label: t('useCases.stats.notPublished') },
        ]
      : [
          { value: allFeaturedUseCases.length, color: 'text-yellow-600', label: t('useCases.stats.featured') },
          { value: publishedUseCases.length, color: 'text-green-600', label: t('useCases.stats.published') },
          { value: completedUseCases.length, color: 'text-gray-600', label: t('useCases.stats.notPublished') },
        ];
    return (
      <div className="flex items-center gap-5 sm:gap-6">
        {stats.map((s, i) => (
          <div key={i} className="text-center">
            <div className={`text-xl font-bold ${s.color} leading-tight tabular-nums`}>{s.value}</div>
            <div className="text-xs text-gray-500">{s.label}</div>
          </div>
        ))}
      </div>
    );
  };

  const renderOurRequests = () => (
    <div className="space-y-6">
      {/* Our requests management */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{t('useCases.ourRequests.title', { count: ourUseCaseRequests.length })}</h3>
            <p className="text-sm text-gray-600 mt-1">{t('useCases.ourRequests.description')}</p>
          </div>

          <div className="flex items-center space-x-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder={t('useCases.ourRequests.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            >
              <option value="all">{t('useCases.ourRequests.filter.allStatuses')}</option>
              <option value="sent">{t('useCases.ourRequests.filter.requested')}</option>
              <option value="accepted">{t('useCases.ourRequests.filter.promoterApproved')}</option>
              <option value="approved">{t('useCases.ourRequests.filter.approved')}</option>
              <option value="declined">{t('useCases.ourRequests.filter.rejected')}</option>
            </select>

            <div className="flex items-center space-x-1 border border-gray-300 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded transition-colors ${
                  viewMode === 'grid'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                title={t('useCases.ourRequests.viewMode.grid')}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded transition-colors ${
                  viewMode === 'list'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                title={t('useCases.ourRequests.viewMode.list')}
              >
                <List className="w-4 h-4" />
              </button>
            </div>

            <Can I="create" a="Promoter">
              <button
                onClick={() => navigate('/request-use-case')}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span>{t('useCases.ourRequests.sendNew')}</span>
              </button>
            </Can>
          </div>
        </div>

        {/* Requests display */}
        {viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {allOurRequests.map((request) => (
              <div
                key={request.id}
                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => handleRequestClick(request)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start space-x-3">
                    <Avatar
                      src={request.promoterAvatar || undefined}
                      name={request.promoterName}
                      size="md"
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-gray-900 text-sm truncate">{request.promoterName}</h4>
                      <p className="text-xs text-gray-600 truncate">{request.promoterCompany}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {getStatusBadge(request.status)}
                    {/* DEV-91 — expiration removed */}
                  </div>
                </div>

                <div className="space-y-2 mb-3 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('useCases.fields.product')}:</span>
                    <span className="font-medium text-gray-700 truncate ml-1">{request.product}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('useCases.fields.industry')}:</span>
                    <span className="font-medium text-gray-700">{request.industry}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('useCases.fields.companySize')}:</span>
                    <span className="font-medium text-gray-700">{request.companySize}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('useCases.fields.requestDate')}:</span>
                    <span className="font-medium text-gray-700">{new Date(request.requestDate).toLocaleDateString('en-US')}</span>
                  </div>
                  {/* DEV-91 — expiration removed. Reminders (DEV-92) shown below. */}
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('useCases.fields.dealValue')}:</span>
                    <span className="font-medium text-green-600">€{request.conversionValue.toLocaleString()}</span>
                  </div>
                  {request.reminderCount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">{t('useCases.fields.reminders')}:</span>
                      <span className="font-medium text-orange-600">{request.reminderCount}</span>
                    </div>
                  )}
                </div>

                {request.notes && (
                  <div className="bg-gray-50 border border-gray-200 rounded p-2 mb-3">
                    <p className="text-xs text-gray-700 line-clamp-2">{request.notes}</p>
                  </div>
                )}

                <div className="flex items-center space-x-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRequestClick(request);
                    }}
                    className="flex-1 text-blue-600 hover:text-blue-800 text-xs font-medium py-1 px-2 border border-blue-200 rounded hover:bg-blue-50 transition-colors"
                  >
                    {t('useCases.buttons.view')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('useCases.fields.promoter')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('useCases.fields.product')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('useCases.fields.industry')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('useCases.fields.companySize')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('useCases.fields.requestDate')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('useCases.fields.dealValue')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('useCases.fields.status')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('useCases.fields.reminders')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('useCases.fields.notes')}</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('useCases.fields.actions')}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {allOurRequests.map((request) => (
                  <tr
                    key={request.id}
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => handleRequestClick(request)}
                  >
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-3">
                        <Avatar
                          src={request.promoterAvatar || undefined}
                          name={request.promoterName}
                          size="md"
                        />
                        <div>
                          <div className="font-medium text-gray-900 text-sm">{request.promoterName}</div>
                          <div className="text-xs text-gray-500">{request.promoterCompany}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm text-gray-900">{request.product}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-700">{request.industry}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-700">{request.companySize}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-700">{new Date(request.requestDate).toLocaleDateString('en-US')}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-green-600">€{request.conversionValue.toLocaleString()}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {getStatusBadge(request.status)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-center">
                        {request.reminderCount > 0 ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                            {request.reminderCount}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                        {request.lastReminder && (
                          <div className="text-xs text-gray-500 mt-1">
                            {t('useCases.fields.lastPrefix', { date: new Date(request.lastReminder).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) })}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="max-w-xs">
                        {request.notes ? (
                          <p className="text-sm text-gray-700 line-clamp-2">{request.notes}</p>
                        ) : (
                          <span className="text-xs text-gray-400">{t('useCases.fields.noNotes')}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRequestClick(request);
                        }}
                        className="text-blue-600 hover:text-blue-800 text-xs font-medium py-1 px-3 border border-blue-200 rounded hover:bg-blue-50 transition-colors"
                      >
                        {t('useCases.buttons.view')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  const renderPromoterSubmissions = () => (
    <div className="space-y-6">
      {/* Promoter submissions management */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{t('useCases.submissions.title', { count: allPromoterSubmissions.length })}</h3>
            <p className="text-sm text-gray-600 mt-1">{t('useCases.submissions.description')}</p>
          </div>

          <div className="flex items-center space-x-4">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
            >
              <option value="all">{t('useCases.submissions.filter.allStatuses')}</option>
              <option value="COMPLETED">{t('useCases.submissions.filter.completed')}</option>
              <option value="PUBLISHED">{t('useCases.submissions.filter.published')}</option>
              <option value="FEATURED">{t('useCases.submissions.filter.featured')}</option>
            </select>

            <select
              value={repFilter}
              onChange={(e) => setRepFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
            >
              <option value="">{t('useCases.submissions.allReps', 'All reps')}</option>
              {teamMembersData.map(m => (
                <option key={m.id} value={m.id}>{m.firstName} {m.lastName}</option>
              ))}
            </select>

            <button
              onClick={() => {
                const filtered = allPromoterSubmissions.filter(s => {
                  if (filterStatus !== 'all' && s.status !== filterStatus) return false;
                  if (repFilter) {
                    const p = promotersData.find(p => p.id === s.promoterId);
                    if (p?.assignedTeamMember?.id !== repFilter) return false;
                  }
                  return true;
                });
                downloadUseCasesCsv(filtered, `use-cases-submissions-${new Date().toISOString().slice(0, 10)}.csv`);
              }}
              disabled={allPromoterSubmissions.length === 0}
              className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="w-4 h-4" />
              {t('useCases.buttons.exportCsv')}
            </button>
          </div>
        </div>

        {/* Submissions grid */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
            <span className="ml-3 text-gray-600">{t('useCases.submissions.loading')}</span>
          </div>
        ) : allPromoterSubmissions.length === 0 ? (
          <div className="text-center py-12">
            <Lightbulb className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">{t('useCases.submissions.empty')}</p>
          </div>
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {allPromoterSubmissions
            .filter(s => {
              if (filterStatus !== 'all' && s.status !== filterStatus) return false;
              if (repFilter) {
                const p = promotersData.find(p => p.id === s.promoterId);
                if (p?.assignedTeamMember?.id !== repFilter) return false;
              }
              return true;
            })
            .map((submission) => (
            <div 
              key={submission.id} 
              className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => handleUseCaseClick(submission)}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-600 rounded-full flex items-center justify-center text-white font-semibold">
                    {submission.promoterName.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-gray-900">{submission.promoterName}</h4>
                    <p className="text-sm text-gray-600">{submission.promoterCompany || t('useCases.fields.noCompany')}</p>
                  </div>
                </div>

                <div className="flex flex-col items-end space-y-1">
                  <PublishToggle
                    isPublished={submission.status === 'PUBLISHED' || submission.status === 'FEATURED'}
                    isLoading={publishingIds.has(submission.id)}
                    onToggle={() => handleTogglePublish(submission)}
                  />
                  {submission.isFeatured && (
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      {t('useCases.status.featured')}
                    </span>
                  )}
                </div>
              </div>

              <div className="mb-4">
                <h5 className="text-sm font-semibold text-gray-900 mb-1">{submission.useCaseTitle ?? t('useCases.submissions.defaultTitle')}</h5>
              </div>

              <div className="space-y-2 mb-4 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-500">{t('useCases.fields.product')}:</span>
                  <span className="font-medium text-gray-700">{submission.productId || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">{t('useCases.fields.submitted')}:</span>
                  <span className="font-medium text-gray-700">{new Date(submission.createdAt).toLocaleDateString('en-US')}</span>
                </div>
              </div>

              {submission.rejectionReason && (
                <div className="bg-red-50 border border-red-200 rounded p-3 mb-4">
                  <p className="text-xs font-medium text-red-900 mb-1">{t('useCases.submissions.rejectionReason')}</p>
                  <p className="text-xs text-red-700">{submission.rejectionReason}</p>
                </div>
              )}

              <div className="flex items-center space-x-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleUseCaseClick(submission);
                  }}
                  className="flex-1 text-purple-600 hover:text-purple-800 text-sm font-medium py-2 px-3 border border-purple-200 rounded hover:bg-purple-50 transition-colors flex items-center justify-center space-x-1"
                >
                  <FileText className="w-4 h-4" />
                  <span>{t('useCases.buttons.review')}</span>
                </button>
              </div>

              {(submission.status === 'PUBLISHED' || submission.status === 'FEATURED') && (
                <div className="border-t border-gray-100 pt-3 mt-3" onClick={(e) => e.stopPropagation()}>
                  <ShareButtons url={getShareUrl(submission.id)} title={submission.useCaseTitle || t('useCases.submissions.defaultTitle')} compact />
                </div>
              )}
            </div>
          ))}
        </div>
        )}
      </div>
    </div>
  );

  const renderPublished = () => (
    <div className="space-y-6">
      {/* Published use cases */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{t('useCases.published.title', { count: publishedUseCases.length })}</h3>
            <p className="text-sm text-gray-600 mt-1">{t('useCases.published.description')}</p>
          </div>

          <div className="flex items-center space-x-4">
            <button
              onClick={() => downloadUseCasesCsv(allPublishedUseCases, `use-cases-published-${new Date().toISOString().slice(0, 10)}.csv`)}
              disabled={allPublishedUseCases.length === 0}
              className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="w-4 h-4" />
              {t('useCases.buttons.exportCsv')}
            </button>
          </div>
        </div>

        {/* Published use cases grid */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full animate-spin"></div>
            <span className="ml-3 text-gray-600">{t('useCases.published.loading')}</span>
          </div>
        ) : allPublishedUseCases.length === 0 ? (
          <div className="text-center py-12">
            <Lightbulb className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">{t('useCases.published.empty')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {allPublishedUseCases.map((useCase) => (
              <div 
                key={useCase.id} 
                className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => handleUseCaseClick(useCase)}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full flex items-center justify-center">
                      <Lightbulb className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{t('useCases.published.useCaseLabel')}</h4>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className="text-sm text-gray-600">{useCase.productId || 'N/A'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col items-end space-y-1">
                    <PublishToggle
                      isPublished={useCase.status === 'PUBLISHED' || useCase.status === 'FEATURED'}
                      isLoading={publishingIds.has(useCase.id)}
                      onToggle={() => handleTogglePublish(useCase)}
                    />
                    {useCase.isFeatured && (
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        {t('useCases.status.featured')}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-start space-x-3 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                    {useCase.promoterName?.split(' ').map((n: string) => n[0]).join('') || '?'}
                  </div>
                  <div>
                    <h5 className="font-medium text-gray-900 text-sm">{useCase.promoterName}</h5>
                    <p className="text-xs text-gray-600">{useCase.promoterCompany || t('useCases.fields.noCompany')}</p>
                  </div>
                </div>

                <p className="text-gray-700 mb-4 text-sm line-clamp-3">
                  {useCase.content ?
                    (typeof useCase.content === 'string' && useCase.content.startsWith('{') ?
                      (JSON.parse(useCase.content)?.generated_use_case || useCase.content) :
                      useCase.content) :
                    t('useCases.published.noContent')}
                </p>

                <div className="space-y-2 mb-4 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('useCases.fields.product')}:</span>
                    <span className="font-medium text-gray-700">{useCase.productId || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('useCases.fields.publishedOn')}:</span>
                    <span className="font-medium text-gray-700">{useCase.reviewedAt ? new Date(useCase.reviewedAt).toLocaleDateString('en-US') : new Date(useCase.createdAt).toLocaleDateString('en-US')}</span>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUseCaseClick(useCase);
                    }}
                    className="text-green-600 hover:text-green-800 text-sm font-medium py-2 px-3 border border-green-200 rounded hover:bg-green-50 transition-colors flex items-center space-x-1"
                  >
                    <Eye className="w-4 h-4" />
                    <span>{t('useCases.buttons.review')}</span>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/use-case-submission/${useCase.id}`, { state: { testimonial: useCase } });
                    }}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium py-2 px-3 border border-blue-200 rounded hover:bg-blue-50 transition-colors flex items-center space-x-1"
                  >
                    <ExternalLink className="w-4 h-4" />
                    <span>{t('useCases.buttons.view')}</span>
                  </button>
                </div>

                <div className="border-t border-gray-100 pt-3 mt-3" onClick={(e) => e.stopPropagation()}>
                  <ShareButtons url={getShareUrl(useCase.id)} title={useCase.useCaseTitle || t('useCases.submissions.defaultTitle')} compact />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderFeatured = () => (
    <div className="space-y-6">
      {/* Featured use cases */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{t('useCases.featured.title', { count: allFeaturedUseCases.length })}</h3>
            <p className="text-sm text-gray-600 mt-1">{t('useCases.featured.description')}</p>
          </div>

          <div className="flex items-center space-x-4">
            <button
              onClick={() => downloadUseCasesCsv(allFeaturedUseCases, `use-cases-featured-${new Date().toISOString().slice(0, 10)}.csv`)}
              disabled={allFeaturedUseCases.length === 0}
              className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="w-4 h-4" />
              {t('useCases.buttons.exportCsv')}
            </button>
          </div>
        </div>

        {/* Featured use cases grid */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-yellow-600 border-t-transparent rounded-full animate-spin"></div>
            <span className="ml-3 text-gray-600">{t('useCases.published.loading')}</span>
          </div>
        ) : allFeaturedUseCases.length === 0 ? (
          <div className="text-center py-12">
            <Star className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">{t('useCases.featured.empty')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {allFeaturedUseCases.map((useCase) => (
              <div 
                key={useCase.id} 
                className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => handleUseCaseClick(useCase)}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-r from-yellow-500 to-orange-600 rounded-full flex items-center justify-center">
                      <Star className="w-6 h-6 text-white fill-current" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{t('useCases.published.useCaseLabel')}</h4>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className="text-sm text-gray-600">{useCase.productId || 'N/A'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col items-end space-y-1">
                    <PublishToggle
                      isPublished={useCase.status === 'PUBLISHED' || useCase.status === 'FEATURED'}
                      isLoading={publishingIds.has(useCase.id)}
                      onToggle={() => handleTogglePublish(useCase)}
                    />
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      {t('useCases.status.featured')}
                    </span>
                  </div>
                </div>

                <div className="flex items-start space-x-3 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                    {useCase.promoterName?.split(' ').map((n: string) => n[0]).join('') || '?'}
                  </div>
                  <div>
                    <h5 className="font-medium text-gray-900 text-sm">{useCase.promoterName}</h5>
                    <p className="text-xs text-gray-600">{useCase.promoterCompany || t('useCases.fields.noCompany')}</p>
                  </div>
                </div>

                <p className="text-gray-700 mb-4 text-sm line-clamp-3">
                  {useCase.content ?
                    (typeof useCase.content === 'string' && useCase.content.startsWith('{') ?
                      (JSON.parse(useCase.content)?.generated_use_case || useCase.content) :
                      useCase.content) :
                    t('useCases.published.noContent')}
                </p>

                <div className="space-y-2 mb-4 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('useCases.fields.product')}:</span>
                    <span className="font-medium text-gray-700">{useCase.productId || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('useCases.fields.publishedOn')}:</span>
                    <span className="font-medium text-gray-700">{useCase.reviewedAt ? new Date(useCase.reviewedAt).toLocaleDateString('en-US') : new Date(useCase.createdAt).toLocaleDateString('en-US')}</span>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUseCaseClick(useCase);
                    }}
                    className="text-green-600 hover:text-green-800 text-sm font-medium py-2 px-3 border border-green-200 rounded hover:bg-green-50 transition-colors flex items-center space-x-1"
                  >
                    <Eye className="w-4 h-4" />
                    <span>{t('useCases.buttons.review')}</span>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/use-case-submission/${useCase.id}`, { state: { testimonial: useCase } });
                    }}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium py-2 px-3 border border-blue-200 rounded hover:bg-blue-50 transition-colors flex items-center space-x-1"
                  >
                    <ExternalLink className="w-4 h-4" />
                    <span>{t('useCases.buttons.view')}</span>
                  </button>
                </div>

                <div className="border-t border-gray-100 pt-3 mt-3" onClick={(e) => e.stopPropagation()}>
                  <ShareButtons url={getShareUrl(useCase.id)} title={useCase.useCaseTitle || t('useCases.submissions.defaultTitle')} compact />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <PageTitle title={t('useCases.pageTitle')} />
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('useCases.heading')}</h1>
          <p className="text-gray-600 mt-1">{t('useCases.subheading')}</p>
        </div>
        {renderInlineStats()}
      </div>

      {/* Navigation tabs */}
      <nav className="flex space-x-1 bg-gray-200 rounded-xl p-1 mb-6">
        <button
          onClick={() => setActiveTab('our_requests')}
          className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium text-sm transition-all duration-200 ${
            activeTab === 'our_requests'
              ? 'bg-blue-600 text-white shadow-md'
              : 'text-gray-600 hover:text-blue-700 hover:bg-white hover:bg-opacity-50'
          }`}
        >
          <Send className="w-4 h-4" />
          <span>{t('useCases.tabs.ourRequestsWithCount', { count: ourUseCaseRequests.length })}</span>
        </button>

        <button
          onClick={() => setActiveTab('promoter_submissions')}
          className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium text-sm transition-all duration-200 ${
            activeTab === 'promoter_submissions'
              ? 'bg-purple-600 text-white shadow-md'
              : 'text-gray-600 hover:text-purple-700 hover:bg-white hover:bg-opacity-50'
          }`}
        >
          <FileText className="w-4 h-4" />
          <span>{t('useCases.tabs.promoterSubmissionsWithCount', { count: allPromoterSubmissions.length })}</span>
        </button>

        <button
          onClick={() => setActiveTab('published')}
          className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium text-sm transition-all duration-200 ${
            activeTab === 'published'
              ? 'bg-green-600 text-white shadow-md'
              : 'text-gray-600 hover:text-green-700 hover:bg-white hover:bg-opacity-50'
          }`}
        >
          <CheckCircle className="w-4 h-4" />
          <span>{t('useCases.tabs.publishedWithCount', { count: publishedUseCases.length })}</span>
        </button>

        <button
          onClick={() => setActiveTab('featured')}
          className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium text-sm transition-all duration-200 ${
            activeTab === 'featured'
              ? 'bg-yellow-600 text-white shadow-md'
              : 'text-gray-600 hover:text-yellow-700 hover:bg-white hover:bg-opacity-50'
          }`}
        >
          <Star className="w-4 h-4" />
          <span>{t('useCases.tabs.featuredWithCount', { count: allFeaturedUseCases.length })}</span>
        </button>
      </nav>

      <div>
        {activeTab === 'our_requests' && renderOurRequests()}
        {activeTab === 'promoter_submissions' && renderPromoterSubmissions()}
        {activeTab === 'published' && renderPublished()}
        {activeTab === 'featured' && renderFeatured()}
      </div>
    </div>
  );
};

export default UseCases;